import type { ComponentProps } from "react";
import { useEffect, useRef, useState } from "react";

import { Text } from "@similarweb/ui-components/foundations";

import useSetTimeout from "app/pages/sales-intelligence/hooks/useSetTimeout";

const DEFAULT_CHAR_INTERVAL_MS = 20;
const DEFAULT_TEXT = "";

export enum ChunkMode {
    Char = "char",
    Word = "word",
}

type Props = Omit<ComponentProps<typeof Text>, "children"> & {
    isAnimate: boolean;
    chunkMode?: ChunkMode;
    charIntervalMs?: number;
    children: string;
};

const splitText = (text: string, mode: ChunkMode): string[] => {
    if (!text) {
        return [];
    }
    if (mode === ChunkMode.Char) {
        return text.split("");
    }

    return text.split(/(\s+)/).filter((part) => part.length > 0);
};

const getDelayMs = (chunk: string, mode: ChunkMode, baseInterval: number): number => {
    if (mode === ChunkMode.Word) {
        return Math.max(1, chunk.length) * baseInterval;
    }

    return baseInterval;
};

const EmulatedStreamingText = ({
    children,
    isAnimate,
    chunkMode = ChunkMode.Char,
    charIntervalMs = DEFAULT_CHAR_INTERVAL_MS,
    ...textProps
}: Props) => {
    const [displayedText, setDisplayedText] = useState(DEFAULT_TEXT);

    const queueRef = useRef<string[]>([]);
    const timerRef = useRef<number | null>(null);

    const setTimeout = useSetTimeout();

    const tick = () => {
        timerRef.current = null;

        if (queueRef.current.length === 0) {
            return;
        }

        const chunk = queueRef.current.shift();
        if (!chunk) {
            return;
        }

        setDisplayedText((prev) => prev + chunk);

        const delay = getDelayMs(chunk, chunkMode, charIntervalMs);
        timerRef.current = setTimeout(tick, delay);
    };

    const startAnimation = () => {
        if (timerRef.current !== null) {
            return;
        }
        timerRef.current = setTimeout(tick, charIntervalMs);
    };

    useEffect(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (!isAnimate) {
            queueRef.current = [];
            setDisplayedText(children);
            return;
        }

        setDisplayedText(DEFAULT_TEXT);

        queueRef.current = splitText(children, chunkMode);
        startAnimation();
    }, [children, isAnimate]);

    return <Text {...textProps}>{displayedText}</Text>;
};

export default EmulatedStreamingText;
